import { Col, Row } from '@op/opds.opux';
import { space } from '@op/opds.tokens';
import { CSSProperties } from 'react';
import { ContentBlock, NestedStrArray } from '../interfaces/ContentBlock';
import DynamicElement from '../util/DynamicElement';
import { HeadingType } from '../types';

const isNestedArray = (
  strOrArray: string | NestedStrArray,
): strOrArray is NestedStrArray => {
  return strOrArray instanceof Array;
};
// previously prefix "$" was used for a centered text-title-medium styling
// but now all subtitles have the same styling
const isSubtitle = (s: string): boolean => s.charAt(0) === '!' || s.charAt(0) === '$';
const formatSubtitle = (s: string): string => s.slice(1);
const hasBoldedBeginning = (s: string): boolean => s.charAt(0) === '#';

const getContentText = (items: NestedStrArray, headingAs: HeadingType): JSX.Element => {
  const SubHeading = headingAs;
  return (
    <>
      {items.map((item: string | NestedStrArray) => {
        if (isNestedArray(item)) {
          return (
            <ul key={item.join()} className="opux-v6__opds">
              {item.map((strOrArray: string | NestedStrArray) => {
                if (typeof strOrArray !== 'string') return null; // TODO: support for nested lists
                return (
                  <li key={strOrArray} className="investoracademy-text-paragraph">
                    {strOrArray}
                  </li>
                );
              })}
            </ul>
          );
        }
        if (isSubtitle(item)) {
          return (
            <SubHeading key={item} className="investoracademy-sub-title">
              {formatSubtitle(item)}
            </SubHeading>
          );
        }
        if (hasBoldedBeginning(item)) {
          const boldEnd = item.lastIndexOf('$');
          const bolded = item.substring(1, boldEnd);
          const rest = item.slice(boldEnd + 1);
          return (
            <p key={item} className="investoracademy-text-paragraph">
              <b>{bolded}</b>
              {rest}
            </p>
          );
        }
        return (
          <p key={item} className="investoracademy-text-paragraph">
            {item}
          </p>
        );
      })}
    </>
  );
};

const ContentHeader = ({
  image,
  title,
  headingAs: Heading,
  style,
}: {
  image?: string;
  title?: string;
  headingAs: HeadingType;
  style?: CSSProperties;
}): JSX.Element => {
  if (title && title.startsWith('%')) {
    return (
      <>
        {image ? <DynamicElement fileName={image} size="large" /> : false}
        <Heading className="investoracademy-title-hero">
          {title.slice(1, title.length)}
        </Heading>
      </>
    );
  }
  return (
    <>
      {image ? <DynamicElement fileName={image} /> : false}
      {title ? (
        <Heading
          style={{ textAlign: 'center', marginBottom: space.default, ...style }}
          className="investoracademy-title-paragraph"
        >
          {title}
        </Heading>
      ) : (
        false
      )}
    </>
  );
};

const NarrowContent = ({
  content,
  className,
  headingAs,
  headingStyle,
  subHeadingAs,
}: {
  content: ContentBlock;
  className: string;
  headingAs: HeadingType;
  headingStyle?: CSSProperties;
  subHeadingAs: HeadingType;
}): JSX.Element => {
  return (
    <Col md={6} className={className} style={{ marginTop: space.large }}>
      <ContentHeader
        image={content.image}
        title={content.title}
        headingAs={headingAs}
        style={headingStyle}
      />
      {getContentText(content.body, subHeadingAs)}
    </Col>
  );
};

const getBodySplitIndex = (body: NestedStrArray): number => {
  if (body.length < 2) {
    return body.length; // forces everything to the first column
  }
  const index = Math.ceil(body.length / 2);
  const firstHalfLastItem = body[index - 1];
  if (typeof firstHalfLastItem === 'string' && isSubtitle(firstHalfLastItem)) {
    // move subtitle to the same column as the following paragraph
    return index - 1;
  }
  return index;
};

const NormalContent = ({
  content,
  headingAs,
  headingStyle,
  subHeadingAs,
}: {
  content: ContentBlock;
  headingAs: HeadingType;
  headingStyle?: CSSProperties;
  subHeadingAs: HeadingType;
}): JSX.Element => {
  const hasHeader = content.image || content.title;
  const splitIndex = getBodySplitIndex(content.body);
  return (
    <>
      {hasHeader && (
        <Col xs={12} style={{ marginTop: space.large, padding: 0 }}>
          <ContentHeader
            image={content.image}
            title={content.title}
            headingAs={headingAs}
            style={headingStyle}
          />
        </Col>
      )}
      {content.width === 'wide' ? (
        <>
          <Col md={6} xs={12} className="investoracademy-multi-column-left-small-padding">
            {getContentText(content.body.slice(0, splitIndex), subHeadingAs)}
          </Col>
          <Col
            md={6}
            xs={12}
            className="investoracademy-multi-column-right-small-padding"
          >
            {getContentText(content.body.slice(splitIndex), subHeadingAs)}
          </Col>
        </>
      ) : (
        <Col
          xs={12}
          style={{
            textAlign: content.alignLeft === 'true' ? 'left' : 'center',
            padding: 0,
          }}
        >
          {getContentText(content.body, subHeadingAs)}
        </Col>
      )}
    </>
  );
};

const subHeadingMap: Record<HeadingType, HeadingType> = {
  h2: 'h3',
  h3: 'h4',
  h4: 'h5',
  h5: 'h6',
  h6: 'h6',
};

const CourseContent = ({
  content,
  headingAs = 'h3',
  headingStyle,
}: {
  content: ContentBlock[];
  headingAs?: HeadingType;
  headingStyle?: CSSProperties;
}): JSX.Element => {
  let narrowIndex = 0;
  let normalIndex = 0;
  const subHeadingAs = subHeadingMap[headingAs];

  return (
    <div className="investoracademy-content-wrapper">
      <Row style={{ margin: 0 }}>
        {content.map((c) => {
          if (c.width === 'narrow') {
            narrowIndex += 1;
            const className = `investoracademy-narrow-column-${
              narrowIndex % 2
                ? 'odd investoracademy-multi-column-left'
                : 'even investoracademy-multi-column-right'
            }`;
            return (
              <NarrowContent
                key={`narrowIndex${narrowIndex}`}
                content={c}
                className={className}
                headingAs={headingAs}
                headingStyle={headingStyle}
                subHeadingAs={subHeadingAs}
              />
            );
          }
          normalIndex += 1;
          return (
            <NormalContent
              key={`normalIndex${normalIndex}`}
              content={c}
              headingAs={headingAs}
              subHeadingAs={subHeadingAs}
              headingStyle={headingStyle}
            />
          );
        })}
      </Row>
    </div>
  );
};

export default CourseContent;
